import { useEffect, useState } from "react";
import { Button, InlineAlert, SearchableSelect, SearchInput, UniversalPageLayout } from "../../components/BaseComponents";
import useApi from "../../hooks/useApi";
import useCleanup from "../../hooks/useCleanup";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import { formatDate, getApiErrorMessage } from "../../lib/utils";
import Label from "../../components/BaseComponents/Label";
import JurisdictionSelect from "../../components/BaseComponents/JurisdictionSelect";
import usePagination from "../../hooks/usePagination";
import useDataSort from "../../hooks/useDataSort";
import { useDebouncedFilters } from "../../hooks/useDebouncedFilters";
import { generatePath, Link } from "react-router-dom";
import routes from "../../components/Router/routes";
import { TEXT_LINK_FOCUS_RING } from "../../lib/a11yFocus";
import NumericText from "../../components/BaseComponents/NumericText/NumericText";

const renderEmission = (key: string, data: any) => {
	return <span><NumericText value={data[key]} /> kgCO₂e</span>
}

const columns: TableColumn[] = [
    {
        id: 'companyName',
        label: 'Organisation',
		sortable: true,
		renderer: (key, data) => {
			return (
				<Link
					className={`text-link ${TEXT_LINK_FOCUS_RING}`}
					to={generatePath(routes.publicProfile.path, {companyId: data.companyId})}
				>
					{ data[key] }
				</Link>
			)
		}
    },
    {
        id: 'companyNumber',
        label: 'Organisation #',
		sortable: true,
    },
    {
        id: 'jurisdictionCode',
        label: 'Location',
		sortable: true,
    },
    {
        id: 'end_date',
        label: 'Period',
        renderer: (key, data) => (
			<span>
				{ formatDate(data.start_date) }
				{' - '}
				{ formatDate(data.end_date) }
			</span>
		),
		sortable: true,
    },
    {
        id: 'scope1_total',
        label: 'Scope 1',
		renderer: renderEmission,
		sortable: true,
    },
    {
        id: 'scope2_total',
        label: 'Scope 2',
		renderer: renderEmission,
		sortable: true,
    },
    {
        id: 'scope3_total',
        label: 'Scope 3',
		renderer: renderEmission,
		sortable: true,
    },
    {
        id: 'total_emissions',
        label: 'Total',
		renderer: renderEmission,
		sortable: true,
    },
    {
        id: 'intensity_ratio',
        label: 'Intenstity',
		renderer: (key, data) => (
			<span>
				<NumericText value={Number(data[key]).toFixed(6)} /> kgCO₂e/EUR
			</span>
		),
		sortable: true,
    },
]

function PublicReports() {
    const api = useApi();
    const [data, setData] = useState([]);

	const pagination = usePagination(1, 20);
	const dataSort = useDataSort('companyName', 'asc');
	const [loading, setLoading] = useState(true);

	const [filters, setFilters] = useState({
		companyName: '',
		companyNumber: '',
		jurisdictionCode: '',
		year: '',
	})
	const [loadError, setLoadError] = useState<any>();

	// Issue #884: this GET sits behind Auth/RequireWriteAccess and can receive the now-rejecting
	// ACCESS_AUDIT_UNAVAILABLE (#857 item 6), or any other failure, instead of hanging forever.
	// onSearch previously had no catch at all, so a rejection left `loading` stuck true, left the
	// table showing whatever (possibly empty/stale) data it already had as if that were the real
	// result, and produced an unhandled rejection. useCleanup also makes the cancellation flag
	// apply on unmount automatically, since useDebouncedFilters' debounce timer and flush() both
	// discard the cleanup function this used to return.
	const onSearch = useCleanup(() => {
		let cancelled = false;
		setLoading(true)
		const run = async () => {
			const qs = new URLSearchParams({
				...filters,
				...pagination.params,
				...dataSort.params,
			})
			const results = await api.get('secr/search?' + qs.toString());
			if(cancelled) {
				return;
			}
			setData(results.data.reports);
			pagination.readMeta(results.data.meta);
			setLoadError(undefined);
			setLoading(false);
		}
		run().catch((err) => {
			if(cancelled) {
				return;
			}
			setLoading(false);
			setLoadError(err);
		})
		return () => {
			cancelled = true;
		}
    })

	const { searchInProgress, flush: flushSearch } = useDebouncedFilters(filters, onSearch)

    useEffect(() => {
        onSearch()
		// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSort.params, pagination.params])

	const startYear = 2010;
	const years = [];
	for(let i = startYear; i <= new Date().getFullYear(); i++) {
		years.unshift(i)
	}
	const yearOptions = [
		{ value: '', label: 'Any' },
		...years.map((year) => ({ value: String(year), label: String(year) })),
	]

    return (
        <UniversalPageLayout
            heading={{ title: "Reports" }}
            filtersSimple={
                <>
                    <SearchInput
                        name="companyName"
                        label="Organisation Name"
                        placeholder="iSumio..."
                        value={filters.companyName}
                        onChange={e => setFilters({...filters, companyName: e.target.value})}
                        onSearch={flushSearch}
                        onClear={() => setFilters({...filters, companyName: ''})}
                        isLoading={searchInProgress}
                    />
                    <SearchInput
                        name="companyNumber"
                        label="Organisation Number"
                        placeholder="123..."
                        value={filters.companyNumber}
                        onChange={e => setFilters({...filters, companyNumber: e.target.value})}
                        onSearch={flushSearch}
                        onClear={() => setFilters({...filters, companyNumber: ''})}
                        isLoading={searchInProgress}
                    />

                    <label>
                        <Label className="block">Years</Label>
                        <SearchableSelect
                            options={yearOptions}
                            value={yearOptions.find((option) => option.value === filters.year)}
                            onChange={(option) => setFilters({...filters, year: option?.value ?? ''})}
                        />
                    </label>
                    <label>
                        <Label className="block">Jurisdiction</Label>
                        <JurisdictionSelect
                            value={filters.jurisdictionCode}
                            onChange={value => setFilters({...filters, jurisdictionCode: value})}
                        />
                    </label>
                </>
            }
            actions={
                <Button
                    type='Primary'
                    text='Search'
                    size='sm'
                    hasTrailingIcon
                    onClick={flushSearch}
                />
            }
        >
			{loadError ? (
				<>
					<InlineAlert type='error' data-testid='secr-search-load-error'>
						{getApiErrorMessage(loadError, 'Failed to load reports. Please try again.')}
					</InlineAlert>
					<Button type='Secondary' text='Retry' additionalClass='mt-4' onClick={() => onSearch()} />
				</>
			) : (
				<>
					<SimpleSortTable
						isLoading={loading || searchInProgress}
						additionalClass="mt-4"
						columns={columns}
						data={data}
						defaultSorting={dataSort.defaultSorting}
						onSortPress={dataSort.onChange}
					/>
					<Pagination
						isLoading={loading}
						pagination={pagination.pagination}
						onPageChange={pagination.onPageChange}
					/>
				</>
			)}
        </UniversalPageLayout>
    )
}

export default PublicReports;
